import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import s from "./Profile.module.scss";
import { Button } from "antd"; // Import message for notifications
import useSWR from "swr";
import {
  parseClientError,
  swrFetcher,
  swrMutationFetcher,
  SwrMutationFetcherArgType,
} from "../../../../../../libs/api/Endpoints";
import { UseAuthToken } from "../../../../../../libs/api/userSession";
import Camera from "../../../../../../global components/Icons/Camera/Camera";
import ContactAvatar from "../../../../../../global components/Icons/ContactAvatar/ContactAvatar";
import useSWRMutation from "swr/mutation";
import { Avatar, UserData } from "../../../../../../libs/Utils/Types/User";
import { SuccessDataType } from "../../../../../../libs/api/types";
import { generateResult } from "../../../../../../global components/ResultModal/ResultModal";
import { ThreeDots } from "react-loader-spinner";

type Inputs = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNo: string;
  avatar: Avatar;
};
type profileUploadResponse = {
  secureUrl: string | undefined;
  publicId: string | undefined;
};
function Profile() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [edit, setEdit] = useState<boolean>(false);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>("");
  const [, /*avatarID*/ setAvatarID] = useState<string | undefined>("");
  const [avatar, setAvatar] = useState<File>();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    getValues,
  } = useForm<Inputs>({
    defaultValues: {
      avatar: { publicId: "", url: "" }, // Ensure avatar is always an object
    },
  });
  const token = UseAuthToken();

  // Fetch user data
  const { data, isLoading, error, mutate } = useSWR<
    { data: UserData },
    any,
    any
  >(token ? ["api/admin/me", token] : null, swrFetcher, {
    revalidateOnFocus: true, // Refetch when the window regains focus
    shouldRetryOnError: false, // Disable retrying on errors
  });

  // Mutation for editing user details
  const { trigger: editDetails } = useSWRMutation(
    "api/admin/me",
    swrMutationFetcher,
  );
  const { trigger: uploadProfile } = useSWRMutation(
    "api/admin/medias",
    swrMutationFetcher,
  );
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatar(file);
      const objectUrl = URL.createObjectURL(file); // Create a URL for the image
      setValue("avatar", {
        publicId: file.name,
        url: objectUrl, // Set the URL for preview purposes
      });
      setAvatarUrl(objectUrl); // Update the state for preview
      e.currentTarget.value = ""; // Reset the file input value
    }
  };

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      //const formData = new FormData();
      const avatarFormData = new FormData();
      let avatarPayload: Record<string, unknown> = {};

      // Append user details to form data

      // Upload avatar if exists
      if (avatar) {
        avatarFormData.append("file", avatar);
        avatarFormData.append("type", "image");
        const response: SuccessDataType<profileUploadResponse> =
          await uploadProfile({
            token,
            requestOption: {
              body: avatarFormData,
              method: "POST",
            },
          });

        avatarPayload = {
          avatar: {
            publicId: response.data.publicId,
            url: response.data.secureUrl,
          },
        };
      }
      const fetchArg: SwrMutationFetcherArgType = {
        token,
        requestOption: {
          body: JSON.stringify({
            ...(data.firstName && { firstName: data.firstName }),
            ...(data.lastName && { lastName: data.lastName }),
            ...(data.phoneNo && { phoneNumber: data.phoneNo }),
            ...avatarPayload,
          }),
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
        },
      };
      const result: SuccessDataType<any> = await editDetails(fetchArg);
      generateResult("success", result.message.toUpperCase());
      mutate();

      if (data.phoneNo) {
        localStorage.setItem("phoneNo", data.phoneNo);
      }
      setEdit(false);
      mutate();
    } catch (error) {
      const e = parseClientError(error);
      generateResult(
        "error",
        e.message.charAt(0).toUpperCase() + e.message.slice(1) || e.error,
      );
      console.error(e);
    }
  };

  const handleIconClick = () => {
    fileInputRef.current?.click(); // Programmatically click the hidden file input
  };

  useEffect(() => {
    if (!data || edit) return;

    setAvatarUrl(data.data.avatar?.url || "");
    setValue("avatar", data.data.avatar);
  }, [data, edit, setValue]);

  return (
    <div className={s.profileSettings}>
      {isLoading || error ? (
        <div className={s.spin}>
          <ThreeDots
            visible={true}
            height="50"
            width="50"
            color="#d52b1e"
            radius="9"
            ariaLabel="three-dots-loading"
            wrapperStyle={{
              dispaly: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
            wrapperClass=""
          />
        </div>
      ) : (
        <>
          <div className={s.head}></div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={s.editProfile}>
              <div className={clsx(s.inputWrapper, s.avatar)}>
                {avatarUrl ? (
                  <span className={s.imageContainer}>
                    <img
                      src={avatarUrl}
                      alt="Uploaded"
                      className={s.profilePic}
                    />
                  </span>
                ) : (
                  <ContactAvatar />
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  className={clsx(s.hiddenFileInput)}
                  onChange={handleFileChange}
                  accept="image/*"
                  disabled={!edit}
                />
                <div className={clsx(s.iconContainer)}>
                  <Camera onClick={handleIconClick} />
                </div>
              </div>
              {!edit && (
                <button
                  type="button" // Change button type to prevent form submission
                  className={s.editBtn}
                  onClick={() => setEdit((prev) => !prev)}
                  disabled={isSubmitting} // Disable during submission
                >
                  Edit details
                </button>
              )}
            </div>
            <div className={s.form}>
              {/* Form fields */}
              <div className={clsx(s.inputWrapper)}>
                <h5 className={clsx(s.inputLabel)}>First name</h5>
                <input
                  type="text"
                  defaultValue={data?.data.firstName}
                  className={clsx(s.input, {
                    [s.inputError]: errors.firstName,
                  })}
                  disabled={!edit}
                  {...register("firstName")} // Example validation
                />
              </div>

              <div className={clsx(s.inputWrapper)}>
                <h5 className={clsx(s.inputLabel)}>Last name</h5>
                <input
                  type="text"
                  defaultValue={data?.data.lastName}
                  className={clsx(s.input, { [s.inputError]: errors.lastName })}
                  disabled={!edit}
                  {...register("lastName")}
                />
              </div>

              <div className={clsx(s.inputWrapper)}>
                <h5 className={clsx(s.inputLabel)}>Email address</h5>
                <input
                  type="email"
                  defaultValue={data?.data.email}
                  className={clsx(s.input, { [s.inputError]: errors.email })}
                  disabled={!edit}
                  {...register("email")}
                />
              </div>

              <div className={clsx(s.inputWrapper)}>
                <h5 className={clsx(s.inputLabel)}>Phone number</h5>
                <Controller
                  name="phoneNo"
                  control={control}
                  render={({ field }) => (
                    <PhoneInput
                      {...field}
                      country={"ng"}
                      enableSearch={true}
                      placeholder="Enter phone number"
                      onChange={field.onChange}
                      containerClass={s.phoneInputContainer}
                      dropdownClass={s.phoneInputDropdown}
                      disabled={!edit}
                      inputClass={clsx(
                        s.input,
                        { [s.inputError]: errors.phoneNo },
                        s.PhoneInput,
                      )}
                      buttonClass={s.flagDropdown}
                      value={data?.data.phoneNumber || ""}
                    />
                  )}
                />
              </div>

              {edit && (
                <Button
                  type="primary"
                  htmlType="submit"
                  className={s.button}
                  loading={isSubmitting}
                >
                  Save details
                </Button>
              )}
            </div>
          </form>
        </>
      )}
    </div>
  );
}

export default Profile;
